import { useState } from "react";
import {
  AlertTriangle,
  ArrowUpRight,
  Flag,
  RefreshCcw,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type {
  CycleResponse,
  PositionResponse,
  StopMovementResponse,
} from "../../api/client";
import type { FeedEvent } from "../../hooks/useWsConnection";
import { breakerPayload, collapseBreakerRuns } from "@/lib/breakerEvents";
import { formatExitReason } from "../positions/closeLabels";
import { cn } from "@/lib/utils";
import Panel from "@/components/common/Panel";

type RowKind =
  | "cycle"
  | "position"
  | "position-closed"
  | "stop"
  | "stop-rejected"
  | "breaker"
  | "reconcile"
  | "warning"
  | "error";

interface Row {
  ts: number;
  iso: string;
  kind: RowKind;
  /** Libellé court FR (colonne type). */
  label: string;
  detail: string;
  code?: string;
  rawDetail?: string;
}

type Filter = "tout" | "trades" | "stops" | "breakers";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "tout", label: "Tout" },
  { key: "trades", label: "Trades" },
  { key: "stops", label: "Stops" },
  { key: "breakers", label: "Breakers" },
];

const FILTER_KINDS: Record<Exclude<Filter, "tout">, RowKind[]> = {
  trades: ["cycle", "position", "position-closed", "reconcile"],
  stops: ["stop", "stop-rejected"],
  breakers: ["breaker", "warning", "error"],
};

const KIND_META: Record<RowKind, { Icon: LucideIcon; color: string }> = {
  cycle: { Icon: Flag, color: "text-info" },
  position: { Icon: ArrowUpRight, color: "text-buy" },
  "position-closed": { Icon: XCircle, color: "text-muted-foreground" },
  stop: { Icon: ShieldCheck, color: "text-buy" },
  "stop-rejected": { Icon: ShieldAlert, color: "text-warning" },
  breaker: { Icon: AlertTriangle, color: "text-warning" },
  reconcile: { Icon: RefreshCcw, color: "text-warning" },
  warning: { Icon: AlertTriangle, color: "text-warning" },
  error: { Icon: ShieldAlert, color: "text-sell" },
};

interface ActivityFeedProps {
  /** Cycle actif — null en veille (le flux ne montre alors que les événements WS). */
  cycle: CycleResponse | null;
  /** Positions du cycle actif (ouvertes + fermées). */
  positions: PositionResponse[] | null;
  /** Mouvements de stop du cycle actif (acceptés + refus d'élargissement §19.5). */
  stopMovements: StopMovementResponse[] | null;
  /** Buffer d'événements WS (stampés seq). */
  wsEvents: FeedEvent[];
  /** Classes du conteneur Panel — la page pilote le positionnement (rail droit rempli). */
  className?: string;
}

/**
 * LE flux d'activité du dashboard — fusion de l'ex-trio DecisionFeed / timeline de cycle /
 * « Breaker activity ». Cycle actif : lignes synthétisées depuis le REST (source de
 * vérité : positions, stops, cycle) + les breakers / alertes du WS, avec le collapse PV4
 * des rafales de breakers. En veille : les événements WS humanisés. Plus récent en haut.
 */
export default function ActivityFeed({
  cycle,
  positions,
  stopMovements,
  wsEvents,
  className,
}: ActivityFeedProps) {
  const [filter, setFilter] = useState<Filter>("tout");

  const chrono = cycle != null
    ? activeRows(cycle, positions, stopMovements, wsEvents)
    : idleRows(wsEvents);

  const collapsed = collapseBreakerRuns(chrono, (first, last, count) => ({
    ...first,
    detail: `${first.code} ×${count} (${timeOfDay(first.iso)} → ${timeOfDay(last.iso)})${
      last.rawDetail ? ` — ${last.rawDetail}` : ""
    }`,
  }));

  const rows =
    filter === "tout"
      ? collapsed
      : collapsed.filter((r) => FILTER_KINDS[filter].includes(r.kind));
  const newestFirst = [...rows].reverse();

  return (
    <Panel
      title="Activité"
      aria-label="Flux d'activité"
      testId="activity-feed"
      className={className}
      aside={
        <div className="flex items-center gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              data-testid={`activity-filter-${f.key}`}
              className={cn(
                "rounded-md border px-2 py-0.5 text-[0.68rem] font-medium transition-colors",
                filter === f.key
                  ? "border-info/40 bg-info-soft text-info"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      }
      bodyClassName="min-h-0 flex-1 overflow-y-auto p-0"
    >
      {newestFirst.length === 0 ? (
        <p className="text-muted-foreground px-4 py-4 text-sm" data-testid="activity-empty">
          Aucun événement pour l'instant.
        </p>
      ) : (
        <ol className="divide-border divide-y">
          {newestFirst.map((r, i) => {
            const meta = KIND_META[r.kind];
            return (
              <li
                key={`${r.ts}-${i}-${r.label}`}
                className="flex items-start gap-2.5 px-4 py-1.5 text-sm"
                data-testid={`activity-${r.kind}`}
              >
                <meta.Icon className={cn("mt-0.5 size-3.5 shrink-0", meta.color)} aria-hidden />
                <time className="text-muted-foreground w-16 shrink-0 font-mono text-xs tabular-nums">
                  {timeOfDay(r.iso)}
                </time>
                <span className={cn("w-24 shrink-0 text-xs font-semibold", meta.color)}>
                  {r.label}
                </span>
                <span className="text-muted-foreground min-w-0 flex-1 font-mono text-xs break-words">
                  {r.detail}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </Panel>
  );
}

/** Cycle actif : synthèse REST (vérité) + breakers/alertes WS fenêtrés sur le cycle. */
function activeRows(
  cycle: CycleResponse,
  positions: PositionResponse[] | null,
  stopMovements: StopMovementResponse[] | null,
  wsEvents: FeedEvent[],
): Row[] {
  const rows: Row[] = [];

  pushIso(rows, cycle.startedAt, {
    kind: "cycle",
    label: "Cycle démarré",
    detail: `biais ${cycle.bias}`,
  });
  if (cycle.endedAt) {
    pushIso(rows, cycle.endedAt, {
      kind: "cycle",
      label: "Cycle terminé",
      detail: cycle.endReason ?? "—",
    });
  }

  for (const p of positions ?? []) {
    pushIso(rows, p.openedAt, {
      kind: "position",
      label: "Ouverture",
      detail: `${p.direction} ${p.role} @ ${Number(p.entryPrice).toFixed(1)} · ${Number(
        p.sizePerPointEur,
      ).toFixed(1)} €/pt`,
    });
    if (p.closedAt) {
      const synthetic = p.exitReason === "RECONCILED_EXTERNALLY_CLOSED";
      const pnl =
        p.realizedPnlEur == null || !Number.isFinite(Number(p.realizedPnlEur))
          ? null
          : Number(p.realizedPnlEur);
      pushIso(rows, p.closedAt, {
        kind: synthetic ? "reconcile" : "position-closed",
        label: synthetic ? "Réconciliée" : "Clôture",
        detail: `${p.direction} ${p.role} @ ${
          p.closePrice == null ? "—" : Number(p.closePrice).toFixed(1)
        } · ${formatExitReason(p.exitReason)}${pnl == null ? "" : ` · ${signedEur(pnl)}`}`,
      });
    }
  }

  for (const s of stopMovements ?? []) {
    pushIso(rows, s.proposedAt, {
      kind: s.accepted ? "stop" : "stop-rejected",
      label: s.accepted ? "Stop avancé" : "Stop refusé",
      detail: `pos ${s.positionId} ${s.oldStop == null ? "—" : Number(s.oldStop).toFixed(1)} → ${Number(
        s.newStop,
      ).toFixed(1)}${s.accepted ? "" : ` (${s.skipReason ?? "refusé"})`}`,
    });
  }

  const startTs = Date.parse(cycle.startedAt);
  const endTs = cycle.endedAt ? Date.parse(cycle.endedAt) : Number.NaN;
  for (const ev of wsEvents) {
    if (!ev.ts) continue;
    const ts = Date.parse(ev.ts);
    if (Number.isNaN(ts)) continue;
    if (!Number.isNaN(startTs) && ts < startTs) continue;
    if (!Number.isNaN(endTs) && ts > endTs) continue;
    if (ev.type === "circuit_breaker.triggered") {
      const payload = breakerPayload(ev);
      if (payload == null) continue;
      rows.push({
        ts,
        iso: ev.ts,
        kind: "breaker",
        label: "Breaker",
        detail: [payload.code, payload.detail].filter(Boolean).join(" — "),
        code: payload.code,
        rawDetail: payload.detail,
      });
    } else if (ev.type === "algo.warning" || ev.type === "algo.error") {
      rows.push({
        ts,
        iso: ev.ts,
        kind: ev.type === "algo.error" ? "error" : "warning",
        label: ev.type === "algo.error" ? "Erreur algo" : "Alerte algo",
        detail: algoDetail(ev),
      });
    }
  }

  rows.sort((a, b) => a.ts - b.ts);
  return rows;
}

/** Veille : les événements WS humanisés (l'ex-DecisionFeed). */
function idleRows(wsEvents: FeedEvent[]): Row[] {
  const rows: Row[] = [];
  for (const ev of wsEvents) {
    if (!ev.ts) continue;
    const ts = Date.parse(ev.ts);
    if (Number.isNaN(ts)) continue;
    const d = (ev.data ?? {}) as Record<string, unknown>;
    switch (ev.type) {
      case "position.opened":
      case "position.updated":
        rows.push({
          ts,
          iso: ev.ts,
          kind: "position",
          label: ev.type === "position.opened" ? "Ouverture" : "Position m.à.j.",
          detail: [d.direction, d.entryPrice ? `entrée ${d.entryPrice}` : null]
            .filter(Boolean)
            .join(" · "),
        });
        break;
      case "position.closed":
        rows.push({
          ts,
          iso: ev.ts,
          kind: "position-closed",
          label: "Clôture",
          detail: [d.direction, d.exitPrice ? `sortie ${d.exitPrice}` : null]
            .filter(Boolean)
            .join(" · "),
        });
        break;
      case "cycle.started":
      case "cycle.ended":
        rows.push({
          ts,
          iso: ev.ts,
          kind: "cycle",
          label: ev.type === "cycle.started" ? "Cycle démarré" : "Cycle terminé",
          detail: [d.bias, d.endReason].filter(Boolean).join(" · "),
        });
        break;
      case "circuit_breaker.triggered": {
        const payload = breakerPayload(ev);
        if (payload == null) break;
        rows.push({
          ts,
          iso: ev.ts,
          kind: "breaker",
          label: "Breaker",
          detail: [payload.code, payload.detail].filter(Boolean).join(" — "),
          code: payload.code,
          rawDetail: payload.detail,
        });
        break;
      }
      case "algo.warning":
      case "algo.error":
        rows.push({
          ts,
          iso: ev.ts,
          kind: ev.type === "algo.error" ? "error" : "warning",
          label: ev.type === "algo.error" ? "Erreur algo" : "Alerte algo",
          detail: algoDetail(ev),
        });
        break;
      default:
        break;
    }
  }
  rows.sort((a, b) => a.ts - b.ts);
  return rows;
}

/** Payloads backend { code, reason, [direction|dealId|cycleId] } + anciens { message } / { detail }. */
function algoDetail(ev: FeedEvent): string {
  const d = (ev.data ?? {}) as Record<string, unknown>;
  return [
    d.code,
    d.direction,
    d.dealId ? `deal ${String(d.dealId).slice(0, 12)}` : null,
    d.cycleId ? `cycle ${d.cycleId}` : null,
    d.reason ?? d.message ?? d.detail,
  ]
    .filter(Boolean)
    .join(" — ");
}

function pushIso(out: Row[], iso: string, rest: Omit<Row, "ts" | "iso">): void {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return;
  out.push({ ts, iso, ...rest });
}

function signedEur(v: number): string {
  return `${v >= 0 ? "+" : "−"}€${Math.abs(v).toFixed(2)}`;
}

function timeOfDay(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleTimeString("en-GB", { hour12: false });
}
